import React, { FC, useEffect, useState } from 'react';
import { useGetTaxSettings } from '../../../hooks/api/TaxSettings/useGetTaxSettings';
import { useUpdateTaxSettings } from '../../../hooks/api/TaxSettings/useUpdateTaxSettings';
import { MAX_ANNUAL_TAX_RATE, MIN_ANNUAL_TAX_RATE, parseAnnualTaxRate } from '../../../utility/taxSettings';
import { classNames } from '../../../utility/utility';

const TaxSettingsPage: FC = () => {
  const { data: taxSettings, isLoading } = useGetTaxSettings();
  const { mutateAsync: updateTaxSettings, isPending: isSaving, error: saveError } = useUpdateTaxSettings();

  const [ rateInput, setRateInput ] = useState('');
  const [ saved, setSaved ] = useState(false);

  useEffect(() => {
    if (taxSettings) {
      setRateInput(String(taxSettings.annual_tax_rate));
    }
  }, [ taxSettings ]);

  const parsedRate = parseAnnualTaxRate(rateInput);
  const isInvalid = rateInput !== '' && parsedRate === null;

  const handleSave = async () => {
    if (parsedRate === null) return;
    await updateTaxSettings({ annual_tax_rate: parsedRate });
    setSaved(true);
  };

  return (
    <div className="px-2">
      <h1 className="text-base font-semibold leading-6 text-gray-900">Taxes</h1>
      <p className="mt-2 text-sm text-gray-700">
        Set the annual tax rate used to estimate prorated taxes. It is applied to a property's Tax Value and prorated
        by the number of months set on each approach, then included in your max bid and net sheet.
      </p>

      <div className="mt-8 sm:max-w-xs">
        <label htmlFor="annual_tax_rate" className="block text-sm font-medium leading-6 text-gray-900">
          Annual Tax Rate (%)
        </label>
        <div className="mt-2">
          <input
            id="annual_tax_rate"
            name="annual_tax_rate"
            type="text"
            inputMode="decimal"
            placeholder="2.23"
            disabled={isLoading}
            value={rateInput}
            onChange={(e) => {
              setRateInput(e.target.value);
              setSaved(false);
            }}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        <p className={classNames('mt-2 text-sm', isInvalid ? 'text-red-600' : 'text-gray-500')}>
          Enter a rate from {MIN_ANNUAL_TAX_RATE}% to {MAX_ANNUAL_TAX_RATE}% with up to two decimals, for example 2.23.
        </p>
        {saveError && <p className="mt-2 text-sm text-red-600">Could not save your tax rate. Please try again.</p>}
        {saved && <p className="mt-2 text-sm text-green-600">Tax rate saved.</p>}
      </div>

      <div className="mt-6">
        <button
          type="button"
          disabled={isLoading || isSaving || parsedRate === null}
          onClick={handleSave}
          className={classNames(
            'rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
            isLoading || isSaving || parsedRate === null ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-500 hover:cursor-pointer'
          )}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default TaxSettingsPage;
